import * as THREE from 'three';
import { NIGHTMARES, TICK_HZ } from '@somnhold/content';
import { roofFarCount, roofHung, roofLoads, ROOF_HEARTH, sendaGoal, type BattleState } from '@somnhold/sim';
import { pinMark } from './mark-pin.ts';
import { t } from '../i18n.ts';
import { clear, h, setStyle } from '../ui/dom.ts';
import { ICONS } from '../ui/icons.ts';
import type { BridgeActions } from './bridge-hud.ts';

/** Where the fort's marks hang (render's FortPlace anchors, world points). */
export interface FortAnchors {
  eaves: (THREE.Vector3 | null)[];
  hooks: THREE.Vector3[][];
  pile: THREE.Vector3 | null;
  hearth: THREE.Vector3 | null;
  flag: THREE.Vector3 | null;
  torch: THREE.Vector3 | null;
  door: THREE.Vector3 | null;
}

/**
 * The fort on screen (docs/sendas/guerrero.md, chapter 3), inside a path's own plate (game/senda-hud.ts) and over the
 * board:
 *
 * - the climb: how far up the mule is; the troll, its life and whether it mends (it does unless it burns), and the
 *   torch's mark once Kel plants it;
 * - the roof: each slope as a meter to ten (what hangs on it, dark; its snow, white; how many more and it tips); the
 *   wind (the side it loads, how soon it turns, red when its flag shakes); the hearth (lit, its kicks, or out and how
 *   long it has left); the pieces on the ground and on each eave as chips: a chip tapped, then a slope, sends the hero
 *   to hang it there (or «Bajar», back to the pile); over the board each slope's load, tappable, and the hearth's mark
 *   when it is out; the old troll's life, and «¡Enterrado! Pegale» while the snow has it down. In the memory the roof
 *   is Fermín's: the meters and the wind, no pieces.
 *
 * Plain DOM written only when it changes (docs/RENDIMIENTO.md: «El HUD escribe texto y estilos solo si cambian»).
 */
export class FortHud {
  readonly panel: HTMLElement;
  private kind: 'slope' | 'roof';
  private v = new THREE.Vector3();
  // The climb.
  private upFill = h('i');
  private upN = h('b.bh-n');
  private trollRow = h('div.bh-row.fh-troll');
  private trollFill = h('i');
  private trollState = h('span.fh-chipstate');
  private torchMark = h('div.fh-torch', {}, h('span', { html: ICONS.ember }), h('b', {}, t('senda.slope.torch')));
  // The roof.
  private eaves: { el: HTMLButtonElement; segs: HTMLElement[]; n: HTMLElement; sub: HTMLElement; chips: HTMLElement; key: string; mark: HTMLButtonElement }[] = [];
  private wind = h('div.fh-wind');
  private windTo = h('b');
  private windIn = h('small');
  private hearthState = h('span.fh-hearth');
  private lightBtn: HTMLButtonElement | null = null;
  private pileChips = h('div.bh-chips');
  private pileKey = '';
  private note = h('p.bh-note');
  private picked = -1;
  private bossFill = h('i');
  private bossRow = h('div.bh-row.fh-boss');
  private buried = h('span.fh-buried', {}, t('senda.roof.buried'));
  private hearthMark: HTMLButtonElement;
  private last = '';

  constructor(
    s: BattleState,
    private marks: HTMLElement,
    private act: BridgeActions,
  ) {
    this.kind = s.rules?.senda?.slope ? 'slope' : 'roof';
    this.hearthMark = h(
      'button.fh-hearthmark',
      { type: 'button', 'data-test': 'roof-hearth-mark', 'aria-label': t('senda.roof.lightHint'), onpointerup: (e: Event) => (e.stopPropagation(), this.act.mark(ROOF_HEARTH)) },
      h('span', { html: ICONS.ember }),
      h('b', {}, t('senda.roof.light')),
    ) as HTMLButtonElement;
    this.panel = this.kind === 'slope' ? this.buildSlope() : this.buildRoof(s);
  }

  /* ---------- the climb ---------- */

  private buildSlope(): HTMLElement {
    this.marks.append(this.torchMark);
    this.trollRow.append(h('span.bh-ico', { html: ICONS.skull }), h('span.bh-label', {}, t('senda.slope.troll')), h('div.bar.bh-bar.fh-hp', {}, this.trollFill), this.trollState);
    return h(
      'div.bh.fhp-slope',
      {},
      h('div.bh-row', {}, h('span.bh-ico', { html: ICONS.walk }), h('span.bh-label', {}, t('senda.slope.climb')), h('div.bar.bh-bar', {}, this.upFill), this.upN),
      this.trollRow,
    );
  }

  private updateSlope(s: BattleState): void {
    const g = sendaGoal(s);
    const k = Math.max(0, Math.min(1, g.done / Math.max(1, g.need)));
    setStyle(this.upFill, 'width', `${(k * 100).toFixed(1)}%`);
    const n = `${Math.floor(k * 100)} %`;
    if (this.upN.textContent !== n) this.upN.textContent = n;
    const w = s.senda?.slope;
    const e = w?.troll ? s.enemies.find((x) => x.id === w.troll) : undefined;
    const show = !!w?.awake && !!e && e.hp > 0;
    this.trollRow.hidden = !show;
    if (show && e) {
      setStyle(this.trollFill, 'width', `${((100 * e.hp) / e.maxHp).toFixed(1)}%`);
      const burning = s.tick - (e.scorch ?? -100000) < (NIGHTMARES.troll.regen?.burnTicks ?? 0);
      const text = burning ? t('senda.slope.burns') : t('senda.slope.mends');
      if (this.trollState.textContent !== text) this.trollState.textContent = text;
      this.trollState.classList.toggle('burn', burning);
    }
    this.torchMark.classList.toggle('on', !!w?.torch);
  }

  /* ---------- the roof ---------- */

  private buildRoof(s: BattleState): HTMLElement {
    const r = s.rules?.senda?.roof;
    const memory = !!r?.fermin;
    const grid = h('div.fh-eaves');
    for (const side of [0, 1]) {
      const segs = Array.from({ length: r?.tip ?? 10 }, () => h('i'));
      const n = h('b.fh-n');
      const sub = h('small.fh-sub');
      const chips = h('div.bh-chips');
      const el = h(
        `button.fh-eave${side === 0 ? '.hearth' : ''}`,
        { type: 'button', 'data-test': `eave-${side}`, onpointerup: (e: Event) => (e.stopPropagation(), this.toEave(side)) },
        h('span.fh-eave-name', {}, side === 0 ? h('span.fh-fire', { html: ICONS.ember }) : '', t(side === 0 ? 'senda.roof.hearthSide' : 'senda.roof.farSide')),
        h('span.fh-meter', {}, ...segs),
        h('span.fh-nums', {}, n, sub),
        memory ? '' : chips,
      ) as HTMLButtonElement;
      if (memory) el.disabled = true;
      grid.append(el);
      const mark = h(
        `button.fh-load${side === 0 ? '.hearth' : ''}`,
        { type: 'button', 'data-test': `eavemark-${side}`, onpointerup: (e: Event) => (e.stopPropagation(), this.toEave(side)) },
        h('b'),
        h('small', {}, '/ 10'),
      ) as HTMLButtonElement;
      this.marks.append(mark);
      this.eaves.push({ el, segs, n, sub, chips, key: '', mark });
    }
    this.marks.append(this.hearthMark);
    this.wind.append(h('span.bh-ico', { html: ICONS.banner }), h('span.bh-label', {}, t('senda.roof.wind')), this.windTo, this.windIn);
    this.lightBtn = h('button.bh-back.fh-light', { type: 'button', 'data-test': 'roof-light', onpointerup: (e: Event) => (e.stopPropagation(), this.act.mark(ROOF_HEARTH)) }, t('senda.roof.light')) as HTMLButtonElement;
    this.bossRow.append(h('span.bh-ico', { html: ICONS.skull }), h('span.bh-label', {}, t('senda.roof.troll')), h('div.bar.bh-bar.fh-hp', {}, this.bossFill), this.buried);
    this.bossRow.hidden = true;
    const back = h('button.bh-back', { type: 'button', 'data-test': 'eave-pile', onpointerup: (e: Event) => (e.stopPropagation(), this.toEave(2)) }, t('senda.roof.back'));
    return h(
      'div.bh.fhp-roof',
      {},
      grid,
      // The wind and the hearth: two lines on a desktop, one on a phone (ui/sendas.css).
      h('div.fh-weather', {}, this.wind, h('div.bh-row.fh-hearthrow', {}, h('span.bh-ico', { html: ICONS.ember }), h('span.bh-label', {}, t('senda.roof.hearth')), this.hearthState, this.lightBtn)),
      memory ? h('p.fh-fermin', {}, t('senda.roof.fermin')) : h('div.bh-row.bh-pile', {}, h('span.bh-label', {}, t('senda.roof.pile')), this.pileChips, back),
      this.bossRow,
      this.note,
    );
  }

  private pick(i: number): void {
    this.picked = this.picked === i ? -1 : i;
    this.pileKey = '';
    for (const e of this.eaves) e.key = '';
  }

  private toEave(side: number): void {
    if (this.picked < 0) {
      const text = t('senda.roof.pick');
      if (this.note.textContent !== text) this.note.textContent = text;
      this.note.classList.remove('flash');
      void this.note.offsetWidth;
      this.note.classList.add('flash');
      return;
    }
    this.act.mark(this.picked, side);
    this.picked = -1;
    this.pileKey = '';
    for (const e of this.eaves) e.key = '';
  }

  private chip(s: BattleState, i: number, hand: boolean): HTMLButtonElement {
    const p = s.rules?.senda?.roof?.pieces[i];
    return h(
      `button.bh-chip${p?.sack ? '.stone.sack' : ''}${this.picked === i ? '.picked' : ''}${hand ? '.hand' : ''}`,
      { type: 'button', 'data-test': `roof-piece-${i}`, 'aria-label': p?.sack ? t('senda.roof.sack') : '', onpointerup: (e: Event) => (e.stopPropagation(), this.pick(i)) },
      p?.sack ? h('span.bh-stone') : h('span.bh-shield', { html: ICONS.shield }),
      h('b', {}, String(p?.w ?? 0)),
    ) as HTMLButtonElement;
  }

  private updateRoof(s: BattleState): void {
    const r = s.rules?.senda?.roof;
    const w = s.senda?.roof;
    if (!r || !w) return;
    const hand = w.on.indexOf(-2);
    if (hand >= 0 && this.picked !== hand && w.job < 0) this.pick(hand);
    const loads = roofLoads(r, w);
    const hung = roofHung(r, w);
    this.eaves.forEach((e, side) => {
      const load = loads[side] as number;
      const key = `${load}|${w.snow[side]}|${w.on.join(',')}|${this.picked}`;
      if (key === e.key) return;
      e.key = key;
      const hooks = hung.w[side] as number;
      e.segs.forEach((seg, i) => {
        seg.className = i < hooks ? 'hook' : i < load ? 'snow' : '';
      });
      e.n.textContent = `${Math.min(load, r.tip)}/${r.tip}`;
      const left = r.tip - load;
      e.sub.textContent = left <= 1 ? t('senda.roof.tipNow') : t('senda.roof.tipIn', { n: left });
      e.el.classList.toggle('near', left <= 2);
      e.el.classList.toggle('target', this.picked >= 0);
      clear(e.chips);
      w.on.forEach((o, k) => {
        if (o === side) e.chips.append(this.chip(s, k, false));
      });
      (e.mark.firstChild as HTMLElement).textContent = String(Math.min(load, r.tip));
      e.mark.classList.toggle('near', left <= 2);
      e.mark.classList.toggle('target', this.picked >= 0);
    });
    // The wind: the side it loads, how soon it turns (red as the flag shakes).
    const lee = (r.wind[w.wind] as { to: number }).to;
    const to = t('senda.roof.windTo', { side: t(lee === 0 ? 'senda.roof.hearthSide' : 'senda.roof.farSide') });
    if (this.windTo.textContent !== to) this.windTo.textContent = to;
    const next = r.wind[w.wind + 1];
    const soon = !!next && next.t - s.waveT <= r.warn;
    const when = next ? (soon ? t('senda.roof.windSoon') : t('senda.roof.windIn', { s: Math.max(0, Math.ceil((next.t - s.waveT) / TICK_HZ)) })) : '';
    if (this.windIn.textContent !== when) this.windIn.textContent = when;
    this.wind.classList.toggle('warn', soon);
    this.wind.classList.toggle('hearth', lee === 0);
    // The hearth.
    const out = !w.lit;
    const hs = out ? t('senda.roof.out', { s: Math.max(0, Math.ceil((r.out - (s.tick - w.outAt)) / TICK_HZ)) }) : w.kicks > 0 ? t('senda.roof.kicked', { n: w.kicks, of: r.kicks }) : t('senda.roof.lit');
    if (this.hearthState.textContent !== hs) this.hearthState.textContent = hs;
    this.hearthState.classList.toggle('out', out);
    this.hearthState.classList.toggle('kicked', !out && w.kicks > 0);
    if (this.lightBtn) this.lightBtn.hidden = !out;
    this.hearthMark.classList.toggle('on', out);
    this.hearthMark.classList.toggle('sent', w.job === -3);
    // The pile, the hand.
    if (!r.fermin) {
      const pk = `${w.on.join(',')}|${this.picked}`;
      if (pk !== this.pileKey) {
        this.pileKey = pk;
        clear(this.pileChips);
        w.on.forEach((o, k) => {
          if (o === -1 || o === -2) this.pileChips.append(this.chip(s, k, o === -2));
        });
      }
    }
    // The old troll.
    const e = w.troll ? s.enemies.find((x) => x.id === w.troll) : undefined;
    this.bossRow.hidden = !e;
    if (e) {
      setStyle(this.bossFill, 'width', `${((100 * Math.max(0, e.hp)) / e.maxHp).toFixed(1)}%`);
      this.buried.classList.toggle('on', w.buried > 0);
    }
    void roofFarCount;
  }

  update(s: BattleState): void {
    if (this.kind === 'slope') this.updateSlope(s);
    else this.updateRoof(s);
    void this.last;
  }

  place(camera: THREE.Camera, w: number, ht: number, a: FortAnchors | null): void {
    if (!a) return;
    if (this.kind === 'slope') {
      this.pin(this.torchMark, a.torch, camera, w, ht);
      return;
    }
    this.eaves.forEach((e, side) => this.pin(e.mark, a.eaves[side], camera, w, ht));
    this.pin(this.hearthMark, a.hearth, camera, w, ht);
  }

  private pin(el: HTMLElement, p: THREE.Vector3 | null | undefined, camera: THREE.Camera, w: number, ht: number): void {
    if (!p) return setStyle(el, 'display', 'none');
    this.v.copy(p).project(camera);
    if (this.v.z > 1) return setStyle(el, 'display', 'none');
    setStyle(el, 'display', '');
    pinMark(el, (this.v.x * 0.5 + 0.5) * w, (-this.v.y * 0.5 + 0.5) * ht);
  }
}
